import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ArrowRight,
  MessageSquare,
  Share2,
  CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const GalleryLightbox = ({ item, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onPrev, onNext]);

  if (!item) return null;

  const handleShare = () => {
    const message = `Check out this property on HousyHunt:\n*${item.title}*\nLocation: ${item.location}\nPrice: ${item.price}\nView details: ${window.location.origin}${item.targetUrl}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/90 backdrop-blur-xl">
        {/* Backdrop click */}
        <div className="absolute inset-0" onClick={onClose} />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.25 }}
          className="relative z-10 w-full max-w-5xl bg-zinc-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row max-h-[92vh]"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 z-20 w-11 h-11 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center backdrop-blur-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Left: Image with Prev/Next Controls */}
          <div className="relative flex-1 bg-black flex items-center justify-center min-h-[300px] lg:min-h-[520px] overflow-hidden">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover max-h-[500px] lg:max-h-[600px]"
            />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

            {/* Prev button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev();
              }}
              aria-label="Previous"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-white text-white hover:text-black flex items-center justify-center backdrop-blur-md transition-all shadow-lg"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Next button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              aria-label="Next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-white text-white hover:text-black flex items-center justify-center backdrop-blur-md transition-all shadow-lg"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Image Tag */}
            <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2">
              <span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                {item.tag}
              </span>
              <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {item.categoryLabel}
              </span>
            </div>
          </div>

          {/* Right: Details & Action */}
          <div className="w-full lg:w-[380px] p-6 sm:p-8 flex flex-col justify-between overflow-y-auto">
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs uppercase tracking-widest text-red-400 font-bold">
                  {item.categoryLabel}
                </span>
                <button
                  onClick={handleShare}
                  className="text-gray-400 hover:text-white flex items-center gap-1 text-xs"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Share</span>
                </button>
              </div>

              <h2 className="text-2xl font-black text-white leading-tight">
                {item.title}
              </h2>

              <div className="flex items-center gap-2 text-sm text-gray-400 mt-2">
                <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                <span>{item.location}</span>
              </div>

              {/* Price Callout */}
              <div className="mt-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-xs text-gray-400">Price / Rate</p>
                <p className="text-2xl font-black text-white mt-0.5">
                  {item.price}
                </p>
              </div>

              {/* Description */}
              <p className="mt-4 text-sm text-gray-300 leading-relaxed">
                {item.description}
              </p>

              {/* Highlights */}
              <div className="mt-5 space-y-2">
                {item.features?.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-8 space-y-3 pt-4 border-t border-white/10">
              <Link
                to={item.targetUrl}
                onClick={onClose}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-sm shadow-lg shadow-red-600/30 transition-all duration-200 group"
              >
                <span>View Full Page Details</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <button
                onClick={() => {
                  const msg = `Hi HousyHunt, I am interested in exploring *${item.title}* at ${item.location} (${item.price}). Please provide more details or schedule a site visit.`;
                  window.open(
                    `https://wa.me/918088687429?text=${encodeURIComponent(msg)}`,
                    "_blank"
                  );
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-[#25D366]/20 hover:bg-[#25D366] text-[#25D366] hover:text-white border border-[#25D366]/30 font-bold text-sm transition-all duration-200"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Inquire on WhatsApp</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default GalleryLightbox;
